/** Personal data rights: export, erasure, and tracked requests. */
import { api } from '@/services/api'

export interface PrivacyRequestRow {
  id: string
  kind: 'access' | 'erasure' | 'correction' | 'objection'
  status: 'open' | 'done' | 'rejected'
  requester_email: string | null
  subject_student_id: string | null
  details: string | null
  created_at: string | null
  due_at: string
  overdue: boolean
  resolution: string | null
}

export interface EraseResult {
  status: 'erased' | 'requested'
  message?: string
  due_at?: string
}

async function downloadJson(path: string, fileName: string): Promise<void> {
  const response = await api.get(path, { responseType: 'blob' })
  const url = URL.createObjectURL(response.data as Blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.click()
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

export const privacyService = {
  exportMyData: () => downloadJson('/privacy/me/export', 'mes-donnees-appacademia.json'),
  exportPupil: (studentId: string) =>
    downloadJson(`/privacy/pupils/${studentId}/export`, 'donnees-eleve-appacademia.json'),
  async eraseMe(reason?: string): Promise<EraseResult> {
    const { data } = await api.post('/privacy/me/erase', { confirmation: 'SUPPRIMER', reason })
    return data
  },
  async erasePupil(studentId: string, reason?: string): Promise<EraseResult> {
    const { data } = await api.post(`/privacy/pupils/${studentId}/erase`, { confirmation: 'SUPPRIMER', reason })
    return data
  },
  async listRequests(status?: string): Promise<PrivacyRequestRow[]> {
    const { data } = await api.get('/privacy/requests', { params: status ? { status } : {} })
    return data
  },
  async resolveRequest(id: string, status: 'done' | 'rejected', resolution: string): Promise<void> {
    await api.post(`/privacy/requests/${id}/resolve`, { status, resolution })
  },
  async schoolSettings(schoolId: string): Promise<{ sel_ai_analysis: boolean }> {
    const { data } = await api.get(`/privacy/schools/${schoolId}/settings`)
    return data
  },
  async updateSchoolSettings(schoolId: string, settings: { sel_ai_analysis: boolean }): Promise<{ sel_ai_analysis: boolean }> {
    const { data } = await api.put(`/privacy/schools/${schoolId}/settings`, settings)
    return data
  },
  async accessLog(studentId: string, offset = 0): Promise<{ total: number; items: AccessLogRow[] }> {
    const { data } = await api.get(`/privacy/pupils/${studentId}/access-log`, { params: { offset, limit: 50 } })
    return data
  },
}

export interface AccessLogRow {
  at: string | null
  who: string | null
  relation: 'parent' | 'admin' | 'staff'
  action: 'view' | 'change'
  path: string
}

export interface ParentalConsentRequest {
  child_name: string | null
  child_email: string | null
  stated_age: number | null
  expires_at: string | null
  privacy_contact: string
}

/** The link e-mailed to a parent whose child signed up on their own. */
export async function getParentalConsent(token: string): Promise<ParentalConsentRequest> {
  const { data } = await api.get(`/privacy/consent/${encodeURIComponent(token)}`)
  return data
}

export async function answerParentalConsent(
  token: string,
  decision: 'grant' | 'refuse',
): Promise<{ status: 'granted' | 'refused' }> {
  const { data } = await api.post(`/privacy/consent/${encodeURIComponent(token)}`, { decision })
  return data
}
